import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiTrendingUp, FiInfo } = FiIcons;

const MarketVolumeChart = ({ selectedProduct }) => {
  // Mock market volume data for the last 12 months
  const volumeData = [
    { month: 'May 2023', novadoz: 2.2, competitor: 8.1, total: 10.3 },
    { month: 'Jun 2023', novadoz: 2.4, competitor: 8.3, total: 10.7 },
    { month: 'Jul 2023', novadoz: 2.1, competitor: 8.0, total: 10.1 },
    { month: 'Aug 2023', novadoz: 2.6, competitor: 8.5, total: 11.1 },
    { month: 'Sep 2023', novadoz: 2.3, competitor: 8.2, total: 10.5 },
    { month: 'Oct 2023', novadoz: 2.8, competitor: 8.7, total: 11.5 },
    { month: 'Nov 2023', novadoz: 2.5, competitor: 8.4, total: 10.9 },
    { month: 'Dec 2023', novadoz: 2.9, competitor: 8.9, total: 11.8 },
    { month: 'Jan 2024', novadoz: 2.7, competitor: 8.6, total: 11.3 },
    { month: 'Feb 2024', novadoz: 3.1, competitor: 9.2, total: 12.3 },
    { month: 'Mar 2024', novadoz: 2.8, competitor: 8.8, total: 11.6 },
    { month: 'Apr 2024', novadoz: 3.2, competitor: 9.5, total: 12.7 }
  ];

  const maxValue = Math.max(...volumeData.map(d => d.total)) * 1.1;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <SafeIcon icon={FiTrendingUp} className="text-blue-600" />
              Market Volume Trends - Last 12 Months
            </h3>
            <p className="text-gray-600 mt-1">
              Volume analysis for {selectedProduct} (Million Units)
            </p>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
              <span className="text-gray-700">Novadoz</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
              <span className="text-gray-700">Competitors</span>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Chart Container */}
        <div className="relative h-80 bg-gray-50 rounded-lg p-4">
          {/* Y-axis labels */}
          <div className="absolute left-0 top-0 bottom-0 w-12 flex flex-col justify-between text-xs text-gray-600 py-4">
            <span>{maxValue.toFixed(1)}M</span>
            <span>{(maxValue * 0.75).toFixed(1)}M</span>
            <span>{(maxValue * 0.5).toFixed(1)}M</span>
            <span>{(maxValue * 0.25).toFixed(1)}M</span>
            <span>0M</span>
          </div>

          {/* Chart area */}
          <div className="ml-12 mr-4 h-full relative">
            {/* Grid lines */}
            {[0, 0.25, 0.5, 0.75, 1].map((ratio, index) => (
              <div
                key={index}
                className="absolute left-0 right-0 border-t border-gray-200"
                style={{ bottom: `${ratio * 100}%` }}
              ></div>
            ))}

            {/* Data bars */}
            <div className="flex items-end justify-between h-full pt-4">
              {volumeData.map((data, index) => (
                <div key={index} className="flex flex-col items-center gap-1" style={{ width: '7%' }}>
                  {/* Competitor volume */}
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: `${(data.competitor / maxValue) * 100}%` }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                    className="bg-gray-400 rounded-t-sm relative group"
                    style={{ width: '100%' }}
                  >
                    <div className="invisible group-hover:visible absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap">
                      Competitors: {data.competitor}M
                    </div>
                  </motion.div>
                  
                  {/* Novadoz volume */}
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: `${(data.novadoz / maxValue) * 100}%` }}
                    transition={{ delay: index * 0.1 + 0.2, duration: 0.5 }}
                    className="bg-blue-600 rounded-t-sm relative group -mt-1"
                    style={{ width: '100%' }}
                  >
                    <div className="invisible group-hover:visible absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-blue-800 text-white text-xs rounded whitespace-nowrap">
                      Novadoz: {data.novadoz}M
                    </div>
                  </motion.div>

                  {/* Month label */}
                  <span className="text-xs text-gray-600 mt-2 transform -rotate-45 origin-left whitespace-nowrap">
                    {data.month.split(' ')[0]}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <SafeIcon icon={FiInfo} className="text-blue-600" />
              <h4 className="font-medium text-blue-900">Our Market Share</h4>
            </div>
            <p className="text-2xl font-bold text-blue-900">25.2%</p>
            <p className="text-sm text-blue-700">Average over 12 months</p>
          </div>

          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <SafeIcon icon={FiTrendingUp} className="text-green-600" />
              <h4 className="font-medium text-green-900">Growth Rate</h4>
            </div>
            <p className="text-2xl font-bold text-green-900">+15.3%</p>
            <p className="text-sm text-green-700">YoY volume growth</p>
          </div>

          <div className="bg-purple-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <SafeIcon icon={FiTrendingUp} className="text-purple-600" />
              <h4 className="font-medium text-purple-900">Total Market</h4>
            </div>
            <p className="text-2xl font-bold text-purple-900">134.5M</p>
            <p className="text-sm text-purple-700">Units (Last 12 months)</p>
          </div>

          <div className="bg-orange-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <SafeIcon icon={FiTrendingUp} className="text-orange-600" />
              <h4 className="font-medium text-orange-900">Our Volume</h4>
            </div>
            <p className="text-2xl font-bold text-orange-900">33.9M</p>
            <p className="text-sm text-orange-700">Units (Last 12 months)</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketVolumeChart;